import React from 'react';

import { useFreecodeContext, useAppContext } from 'hooks/useContext';
import { buttonIds } from 'components/FreecodePage/FreecodeLayoutConstants';
import { OptionContainer } from 'components/FreecodeTabSelector/styles';
import { RectangleButton } from 'components/Button/rectangleButton';
import Dropdown from 'components/Dropdown/Dropdown';

export const LanguageDropdown = () => {
  const { test, testImpl, languageData, language, setLanguage, input, setCode, exerciseOptions } = useFreecodeContext();
  const { isDarkMode } = useAppContext();

  const doLanguageSelection = (value) => {
    const desiredLanguage = value.value;
    if (!desiredLanguage) return;
    if (language === desiredLanguage) return;
    if (!languageData[desiredLanguage]) return;
    setCode(
      input?.levelData?.[String(exerciseOptions.levelToRun - 1)]?.[desiredLanguage]?.latestRun?.code ||
        localStorage.getItem(`fsEditor-${test.category}-${test._id}-${desiredLanguage}`) ||
        testImpl.generated?.skeletons?.[desiredLanguage] ||
        'Code not found...'
    );
    localStorage.setItem(`fsEditor-${test.category}-${test._id}-language`, desiredLanguage);
    setLanguage(desiredLanguage);
  };

  return (
    <Dropdown
      id={buttonIds.selectLanguage}
      isDarkMode={isDarkMode}
      options={Object.keys(languageData).map((key) => {
        return { value: key, label: languageData[key].CurrentVersion, icon: languageData[key].icon };
      })}
      icon={languageData[language].icon}
      value={languageData[language].CurrentVersion}
      onChange={doLanguageSelection}
    />
  );
};

const categoryOptions = [
  { value: 'code', label: 'Code Test' },
  { value: 'challenge', label: 'Challenge' },
  { value: 'exercise', label: 'Exercise' },
];

export const CategoryDropdown = () => {
  const { test } = useFreecodeContext();
  const { isDarkMode } = useAppContext();

  return (
    <Dropdown
      isDarkMode={isDarkMode}
      options={categoryOptions}
      value={categoryOptions.find((elem) => test.category === elem.value)?.label ?? test.category}
      onChange={(value) => alert(`category ${value.label} selected but function not implemented`)}
    />
  );
};

const ExerciseResetDropdown = ({ language, exerciseOptions, setCode, testImpl, input }) => {
  const { isDarkMode } = useAppContext();
  let resetOptions = [{ label: 'Initial state', value: 'skeleton' }];

  const levelInputForLang = input?.levelData?.[String(exerciseOptions.levelToRun - 1)]?.[language];
  if (levelInputForLang) {
    if (levelInputForLang.latestRun) resetOptions.push({ label: 'Last run', value: 'latestRun' });
    if (levelInputForLang.highScore) resetOptions.push({ label: 'High score', value: 'highScore' });
    if (levelInputForLang.firstSuccess) resetOptions.push({ label: 'First success', value: 'firstSuccess' });
  }

  const resetCode = (value) => {
    let newCode = 'Code not found...';
    if (value?.value === 'skeleton') newCode = testImpl.generated.skeletons[language];
    else if (levelInputForLang[value?.value]?.code) newCode = levelInputForLang[value?.value].code;
    setCode(newCode);
    localStorage.setItem(`fsEditor-exercise-${testImpl.test}-${language}`, newCode);
  };

  return <Dropdown isDarkMode={isDarkMode} options={resetOptions} value="Reset Code" onChange={resetCode} />;
};

export const LanguageSelection = () => {
  const { isCreator, language, exerciseOptions, test, testImpl, setCode, input } = useFreecodeContext();

  const resetCode = () => {
    let newCode = testImpl.generated?.skeletons?.[language] ?? 'Code not found...';
    setCode(newCode);
    localStorage.setItem(`fsEditor-${test.category}-${test._id}-${language}`, newCode);
  };

  return (
    <OptionContainer>
      {!isCreator && (
        <>
          {LanguageDropdown()}
          {exerciseOptions && (
            <ExerciseResetDropdown
              testImpl={testImpl}
              setCode={setCode}
              exerciseOptions={exerciseOptions}
              language={language}
              input={input}
            />
          )}
          {!exerciseOptions && (
            <RectangleButton id={buttonIds.runButton} small onClick={resetCode}>
              Reset Code
            </RectangleButton>
          )}
        </>
      )}
    </OptionContainer>
  );
};
